<template>
  <div class="graph-container d-flex justify-center align-center flex-column w-100">
    <p class="list__header mx-auto" :class="mdAndDown ? 'mx-auto': 'ml-0 mr-auto'">
      Matrículas
    </p>
    <div style="width: 100%;" class="d-flex align-center">
      <v-select
        class="chart-input ml-2"
        v-model="currentChartType"
        :items="chartTypes"
        item-text="text"
        item-value="value"
        label="Tipo"
        dense
        outlined
        hide-details
        @change="datesUpdated"
      />
      <v-switch
        v-if="currentChartType === 'DAILY'"
        class="ml-6"
        v-model="includeAllDays"
        label="Incluir días sin matrículas"
        hide-details
        density="comfortable"
        @change="datesUpdated"
      />
    </div>
    <p class="amount-summary">
      <template v-if="globalSectionName">
        <b>Sección:</b> {{ globalSectionName }} ·
      </template>
        Total de matrículas desde {{ dateFromLabel }} hasta {{ dateToLabel }}:
        <b>{{ totalMatriculas }}</b>
    </p>
    <ApexChart
      ref="chartRef"
      :width="width"
      :height="height"
      :class="mdAndDown ? 'mx-auto': 'ml-0 mr-auto'"
      :type="currentChartType === 'MONTHLY' ? 'bar' : 'line'"
      :options="options"
      :series="series"
    />
  </div>
</template>

<script>
import axios from 'axios'
import { settingsLinePlot } from '@/utils/graphs/ingresos'

export default {
  name: 'NumberOfMatriculeGraph',
  props: {
    globalCategories: { type: Array, default: () => [] },
    globalSections:   { type: Array, default: () => [] },
    globalCategory: { type: String, default: null },
    globalSection:  { type: [Number, String], default: null },
    globalStartDate:{ type: String, default: null },
    globalEndDate:  { type: String, default: null },
    globalSectionName: { type: String, default: '' }, 
    height: { type: String, default: '230' },
    width:  { type: String, default: '140%' },
  },
  data () {
    const opts = JSON.parse(JSON.stringify(settingsLinePlot.options))
    opts.yaxis = {
      ...(opts.yaxis || {}),
      title: { text: 'Matrículas' }
    }
    opts.xaxis = { ...(opts.xaxis || {}), type: 'text', categories: [] }

    return {
      series: [{ name: 'Matrículas', data: [] }],
      options: opts,

      currentChartType: 'DAILY', // MONTHLY | DAILY
      chartTypes: [
        { value: 'MONTHLY', text: 'Mensual' },
        { value: 'DAILY',   text: 'Diario'  },
      ],
      includeAllDays: true, 

      totalMatriculas: 0,
    }
  },
  filters: {
    moment (v, fmt) {
      if (!v) return ''
      const d = new Date(v)
      const dd = String(d.getDate()).padStart(2, '0')
      const mm = String(d.getMonth() + 1).padStart(2, '0')
      const yyyy = d.getFullYear()
      if (fmt === 'DD/MM/YYYY') return `${dd}/${mm}/${yyyy}`
      return v
    }
  },
  computed: {
    mdAndDown () {
      return this.$vuetify.breakpoint.mdAndDown
    },
   dateFromLabel () {
      if (this.globalStartDate) return this.globalStartDate
      const { start } = this.getDefaultMonthRange()
      return start
    },
    dateToLabel () {
      if (this.globalEndDate) return this.globalEndDate
      const { end } = this.getDefaultMonthRange()
      return end
    },
  },
  watch: {
    globalCategories () { this.datesUpdated() },
    globalSections   () { this.datesUpdated() },
    globalCategory () { this.datesUpdated() },
    globalSection  () { this.datesUpdated() },
    globalStartDate() { this.datesUpdated() },
    globalEndDate  () { this.datesUpdated() },
  },
  methods: {
    formatDate (v) {
    if (!v) return '—'
    const d = new Date(v)
    if (Number.isNaN(d.getTime())) return '—'
    const dd = String(d.getDate()).padStart(2, '0')
    const mm = String(d.getMonth() + 1).padStart(2, '0')
    const yyyy = d.getFullYear()
    return `${dd}/${mm}/${yyyy}`
    },
    buildQueryBase () {
      const params = new URLSearchParams()
      const { start, end } = this.globalStartDate && this.globalEndDate
        ? { start: this.globalStartDate, end: this.globalEndDate }
        : this.getDefaultMonthRange()
      if (start) params.set('startDate', start)
      if (end)   params.set('endDate',   end)
      const cats = Array.isArray(this.globalCategories) && this.globalCategories.length
        ? this.globalCategories
        : (this.globalCategory ? [this.globalCategory] : [])
      const secs = Array.isArray(this.globalSections) && this.globalSections.length
        ? this.globalSections
        : (this.globalSection ? [this.globalSection] : [])
      if (secs.length) {
        secs.forEach(id => params.append('seccion', id))
      } else if (cats.length) {
        cats.forEach(slug => params.append('categoria', slug))
      }
      return params
    },
   getDefaultMonthRange () {
     const toIso = (d) => {
        const yyyy = d.getFullYear()
        const mm = String(d.getMonth() + 1).padStart(2, '0')
        const dd = String(d.getDate()).padStart(2, '0')
        return `${yyyy}-${mm}-${dd}`
      }
      const today = new Date()
      const start = new Date(today.getFullYear(), today.getMonth(), 1)
      const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0)
      return {
        start: toIso(start),
        end: toIso(endOfMonth)
      }
    },
    async fetchMonthly () {
      const params = this.buildQueryBase()
      const { data } = await axios.get(`api/admin/graficas/matriculas_por_mes/?${params.toString()}`)
      return data 
    },
    async fetchDaily () {
      const params = this.buildQueryBase()
      if (this.includeAllDays) params.set('includeEmpty', 'true')
      const { data } = await axios.get(`api/admin/graficas/matriculas_por_dia/?${params.toString()}`)
      return data
    },
    formatMonthly (data) {
      this.totalMatriculas = 0
      const categories = []
      const values = []
      data.forEach(d => {
        categories.push(`${d.mes} ${d.anio}`)
        values.push(d.cantidad)
        this.totalMatriculas += d.cantidad
      })
      this.series[0].data = values
      this.options.xaxis.type = 'text'
      this.options.xaxis.categories = categories
      this.options.chart = { ...(this.options.chart || {}), type: 'bar' }
      this.options.plotOptions = { bar: { columnWidth: '55%', borderRadius: 6 } }
      this.options.dataLabels = { enabled: true }
    },
    formatDaily (data) {
      this.totalMatriculas = 0
      const points = data.map(d => {
        this.totalMatriculas += d.cantidad
        return [new Date(d.fecha).getTime(), d.cantidad]
      })
      this.series[0].data = points
      this.options.xaxis.type = 'datetime'
      this.options.xaxis.categories = []

      this.options.chart = { ...(this.options.chart || {}), type: 'line', zoom: { enabled: false } }
      this.options.dataLabels = { enabled: false }
      this.options.stroke = { width: 3, curve: 'smooth' }
      this.options.markers = { size: 3 }
      this.options.tooltip = { x: { format: 'dd MMM yyyy' } }
    },
    async datesUpdated () {
      try {
        if (this.currentChartType === 'MONTHLY') {
          const data = await this.fetchMonthly()
          this.formatMonthly(data)
        } else {
          const data = await this.fetchDaily()
          this.formatDaily(data)
        }
        this.$refs.chartRef?.refresh && this.$refs.chartRef.refresh()
      } catch (e) {
        console.error('Error fetching matriculas data', e)
      }
    },
  },
  mounted () {
    this.datesUpdated()
  }
}
</script>

<style lang="scss" scoped>
.graph-container {
  background-color: #fff;
  border-radius: 15px;
  padding: 20px 24px;
}
.list {
  &__header {
    font-style: normal;
    font-weight: bold;
    font-size: 18px;
    line-height: 21px;
    color: #455A64;
  }
}
.chart-input { max-width: 220px; }
.amount-summary {
  font-size: 13px;
  color: #455A64;
  align-self: self-start;
}
</style>
